// The Agents menu (APPLE-SPEC §5.4; 16 Oct 2026): 440 px (448 in Arabic), radius 8, padding 5, its text column aligned with the "Agents"
// label. The header "All agents" opens the Agents page. One 40 px row per agent in the Ministry's order — a 12 px tick column, the 16 px
// glyph, the persona over the product in a 148 px column ("The Librarian" / "Knowledge Bank"; 21 Oct 2026) and a one-line purpose in
// --label-2-on (the longer purpose is its tooltip) — the
// Knowledge Bank first, then a separator and the other five. No dots, counts or status: the menu only switches agents. The current agent
// is a selected source-list row (grey fill, semibold, ✓, menuitemradio aria-checked); only the pointer or keyboard row is navy. An agent
// the person may not open stays listed, dimmed, with the reason (SPEC C2); one not rolled out to them is left out (WP-TRUST rollout). On a
// phone the menu is a bottom sheet with 44 px rows. WP-SHELL.
import { Check } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useLocale, useTranslation } from "@ncp/i18n";
import { DropdownMenuItem } from "@ncp/ui";
import { MODULES, agentNames, moduleOfPath, useVisibleModules, type ModuleDef } from "../modules";
import { AgentAvatar } from "../brand";

export function AgentsMenuBody({ onNavigate, can }: { onNavigate: () => void; can: (p: string) => boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const ar = locale === "ar";
  const { pathname } = useLocation();
  const visible = useVisibleModules();
  const here = moduleOfPath(pathname);

  const row = (m: ModuleDef) => {
    const allowed = m.permission.some(can);
    const Icon = m.icon;
    const on = here === m.key;
    const names = agentNames(m.key, ar);
    const product = ar ? m.short.ar : m.short.en;
    const purpose = allowed ? t(`assistant:econic.shell.intel.desc.${m.key}`) : t("assistant:econic.shell.intel.noAccess", { module: product });
    const longPurpose = allowed ? t(`assistant:econic.shell.module.purpose.${m.key}`) : purpose;
    const body = (
      <>
        <span className="tick" aria-hidden>{on ? <Check /> : null}</span>
        <AgentAvatar agent={m.key} size={16} faceSize={20} icon={<Icon aria-hidden />} />
        <span className="ml">
          <span className="nm">{names.name}</span>
          {names.sub ? <span className="sb">{names.sub}</span> : null}
        </span>
        <span className="md" title={longPurpose}>{purpose}</span>
      </>
    );
    return (
      <DropdownMenuItem key={m.key} asChild disabled={!allowed} onSelect={onNavigate} role="menuitemradio" aria-checked={on} className="mi" data-module={m.key}>
        {allowed ? <Link to={m.route}>{body}</Link> : <a aria-disabled="true">{body}</a>}
      </DropdownMenuItem>
    );
  };

  const kb = MODULES.find((m) => m.key === "kb" && visible.has(m.key));
  const rest = MODULES.filter((m) => m.key !== "kb" && visible.has(m.key));

  return (
    <div className="es-intel" data-econic-agents-menu="">
      <DropdownMenuItem asChild onSelect={onNavigate} className="mh2">
        <Link to="/intelligence">{t("assistant:econic.shell.intel.heading")}</Link>
      </DropdownMenuItem>
      {kb ? row(kb) : null}
      {kb && rest.length ? <div className="ms" role="separator" /> : null}
      {rest.map(row)}
    </div>
  );
}
